/**
 * Custom React Hook for OpenAI Realtime API Integration
 * Manages WebRTC connection, audio streaming, and event handling
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import i18next from 'i18next'
import { logInfo, logError, logWarn } from '@/lib/logger'
import { realtimeSessionService } from '@/services/realtime-session-service'
import type {
  RealtimeState,
  RealtimeStatus,
  TranscriptItem,
  ConversationItemCreatedEvent,
  ResponseDoneEvent,
  InputAudioBufferSpeechStartedEvent,
  InputAudioBufferSpeechStoppedEvent,
  RealtimeEvent
} from '@/types/realtime'
import { NO_VOICE_LINE, nextVoiceLine, type VoiceLine } from '@/lib/voiceLine'


const OPENAI_REALTIME_URL_FALLBACK = 'https://api.openai.com/v1/realtime'

/**
 * Returns a one-line language instruction to prepend to system instructions.
 */
function getLanguageInstruction(lang: string): string {
  return lang.startsWith('de')
    ? 'Antworte immer auf Deutsch.'
    : 'Always respond in English.'
}

/**
 * Prepares system instructions with the correct language.
 * Removes conflicting language directives from the base instructions (e.g. from cdb_tqs)
 * and prepends the target language instruction.
 */
function buildInstructions(baseInstructions: string, lang: string): string {
  let instructions = baseInstructions.trim()

  // Remove any existing language directives that conflict with the target language
  if (!lang.startsWith('de')) {
    // Strip German language directives so they don't override our English instruction
    instructions = instructions
      .replace(/Antworte[^\n.!?]*auf Deutsch[^\n.!?]*[.!?]?/gi, '')
      .replace(/Always respond in German[^\n.!?]*[.!?]?/gi, '')
      .replace(/in German\s*\(Deutsch\)[.!?]?/gi, '')
      .replace(/in deutscher Sprache[^\n.!?]*[.!?]?/gi, '')
      .replace(/auf Deutsch antworten[^\n.!?]*[.!?]?/gi, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  }

  return `${getLanguageInstruction(lang)}\n\n${instructions}`
}

export function useOpenAIRealtime() {
  // State management
  const [state, setState] = useState<RealtimeState>({
    isConnected: false,
    isListening: false,
    isSpeaking: false,
    isMuted: false,
    transcript: [],
    currentLine: '',
    error: null,
    sessionId: null,
  })
  const [status, setStatus] = useState<RealtimeStatus>('disconnected')
  const [isMinimized, setIsMinimized] = useState(false)
  const [sessionDuration, setSessionDuration] = useState(0)

  // Refs for persistent objects
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null)
  const dataChannelRef = useRef<RTCDataChannel | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const audioStreamRef = useRef<MediaStream | null>(null)
  const audioElementRef = useRef<HTMLAudioElement | null>(null)
  const voiceConfigRef = useRef<any>(null)
  const providerRef = useRef<string>('openai') // Track provider: 'openai' or 'Azure OpenAI'
  const greetingCompletedRef = useRef<boolean>(false)
  const greetingRequestedRef = useRef<boolean>(false) // Track if greeting was already requested
  const sendRealtimeEventRef = useRef<((event: RealtimeEvent) => void) | null>(null)
  const sequenceCounterRef = useRef<number>(0) // Sequence counter for proper message ordering
  const voiceLineRef = useRef<VoiceLine>(NO_VOICE_LINE)

  /**
   * Update a specific part of state
   */
  const updateState = useCallback((updates: Partial<RealtimeState>) => {
    setState(prev => ({ ...prev, ...updates }))
  }, [])

  /**
   * Add a transcript item with sequence number for proper ordering
   */
  const addTranscriptItem = useCallback((item: TranscriptItem) => {
    const itemWithSequence = {
      ...item,
      sequence: sequenceCounterRef.current++
    }
    setState(prev => ({
      ...prev,
      transcript: [...prev.transcript, itemWithSequence],
    }))
  }, [])

  /**
   * Handle incoming Realtime API events
   */
  const handleRealtimeEvent = useCallback((event: RealtimeEvent) => {
    logInfo(`Realtime event received: ${event.type}`)

    const line = nextVoiceLine(voiceLineRef.current, event as RealtimeEvent & { delta?: string; transcript?: string })
    if (line !== voiceLineRef.current) {
      voiceLineRef.current = line
      updateState({ currentLine: line.text })
    }

    switch (event.type) {
      case 'session.created':
        logInfo(`Session created: ${event.session?.id}`)
        updateState({ sessionId: event.session?.id || null })

        // Azure GA: session config is baked into the token, session.update is NOT supported
        // so session.updated will never fire. Trigger greeting directly here.
        if (providerRef.current === 'Azure OpenAI') {
          logInfo('Azure GA provider detected — triggering greeting from session.created (no session.update support)')
          if (voiceConfigRef.current?.greeting_instructions?.trim() && !greetingRequestedRef.current) {
            greetingRequestedRef.current = true

            if (sendRealtimeEventRef.current) {
              sendRealtimeEventRef.current({
                type: 'input_audio_buffer.clear',
              })

              setTimeout(() => {
                if (sendRealtimeEventRef.current) {
                  // Azure GA: session.update is not supported, so greeting instructions
                  // can't be embedded in the session config. Instead, create a conversation
                  // item with the greeting instruction, then trigger response.create.
                  const greetingText = voiceConfigRef.current.greeting_instructions.trim()
                  logInfo(`Azure GA: Sending greeting instruction via conversation.item.create: "${greetingText.substring(0, 80)}..."`)

                  sendRealtimeEventRef.current({
                    type: 'conversation.item.create',
                    item: {
                      type: 'message',
                      role: 'user',
                      content: [{
                        type: 'input_text',
                        text: i18next.t('voiceAgent.azureGreetingWrapper', { greetingText }),
                      }],
                    },
                  })

                  // Small delay to ensure item is created before requesting response
                  setTimeout(() => {
                    if (sendRealtimeEventRef.current) {
                      sendRealtimeEventRef.current({
                        type: 'response.create',
                      })
                    }
                  }, 100)
                }
              }, 100)
            }
          }
        }
        break

      case 'session.updated':
        logInfo('Session updated')

        // OpenAI: Trigger greeting after session is confirmed updated (not before!)
        // This prevents race conditions where response.create is sent before config is applied
        if (voiceConfigRef.current?.greeting_instructions?.trim() && !greetingRequestedRef.current) {
          greetingRequestedRef.current = true
          logInfo('Session confirmed, triggering initial greeting...')

          // Clear any buffered audio first to prevent echo/interruption
          if (sendRealtimeEventRef.current) {
            sendRealtimeEventRef.current({
              type: 'input_audio_buffer.clear',
            })

            // Small delay to ensure buffer is cleared before requesting response
            setTimeout(() => {
              if (sendRealtimeEventRef.current) {
                sendRealtimeEventRef.current({
                  type: 'response.create',
                  response: {
                    modalities: ['audio', 'text'],
                  },
                })
              }
            }, 100)
          }
        }
        break

      case 'response.created':
        logInfo(`Response created: ${event.response?.id}`)
        updateState({ isSpeaking: true })
        setStatus('speaking')
        break

      case 'conversation.item.created': {
        const itemEvent = event as ConversationItemCreatedEvent
        const { item } = itemEvent

        // Extract text content
        const textContent = item.content
          .map(c => c.text || c.transcript)
          .filter(Boolean)
          .join(' ')

        if (textContent) {
          // For user messages with audio input, skip here - will be added via transcription event
          const hasAudioInput = item.content.some(c => c.type === 'input_audio')
          if (item.role === 'user' && hasAudioInput) {
            logInfo(`Skipping user audio item - waiting for transcription: ${item.id}`)
            break
          }

          // Skip assistant messages here - they will be added in response.done with proper ordering
          if (item.role === 'assistant') {
            logInfo(`Skipping assistant item - will be added in response.done: ${item.id}`)
            break
          }

          const transcriptItem: TranscriptItem = {
            id: item.id,
            role: item.role === 'user' ? 'user' : 'assistant',
            content: textContent,
            timestamp: new Date().toISOString(),
            type: item.type === 'function_call' ? 'function_call' : 'message',
          }

          addTranscriptItem(transcriptItem)
        }
        break
      }

      case 'response.done': {
        const doneEvent = event as ResponseDoneEvent
        logInfo(`Response done: ${doneEvent.response.status}`)

        // Extract assistant response
        let fullTextContent = ''
        const baseTimestamp = Date.now()

        doneEvent.response.output.forEach((output, index) => {
          const textContent = output.content
            .map(c => c.text || c.transcript)
            .filter(Boolean)
            .join(' ')

          if (textContent) {
            fullTextContent += textContent + ' '
            const transcriptItem: TranscriptItem = {
              id: output.id,
              role: 'assistant',
              content: textContent,
              // Add small increment to ensure correct ordering when multiple outputs
              timestamp: new Date(baseTimestamp + index).toISOString(),
              type: 'message',
            }

            addTranscriptItem(transcriptItem)
          }
        })

        updateState({ isSpeaking: false })
        setStatus('connected')

        // Enable turn detection and unmute mic after first greeting is completed
        if (!greetingCompletedRef.current) {
          greetingCompletedRef.current = true

          // Estimate greeting duration to prevent self-interruption (echo)
          // Optimized estimate: 0.3s per word + 0.8s buffer (OpenAI TTS is ~200 WPM)
          const words = fullTextContent.split(/\s+/).length || 10
          // Minimum 1.5s, max 6s delay (reduced for better UX)
          const estimatedDuration = Math.min(6000, Math.max(1500, (words * 300) + 800))

          logInfo(`First greeting generation done. Text: "${fullTextContent.trim().substring(0, 50)}..."`)
          logInfo(`Estimated playback duration: ${estimatedDuration}ms (${words} words × 300ms + 800ms buffer). Waiting before enabling turn detection...`)

          setTimeout(() => {
            // Check if still connected
            if (!dataChannelRef.current || dataChannelRef.current.readyState !== 'open') return

            logInfo('Enabling turn detection and unmuting mic after greeting delay')

            // Re-enable microphone after greeting
            if (audioStreamRef.current) {
              audioStreamRef.current.getAudioTracks().forEach(track => {
                track.enabled = !state.isMuted
              })
            }

            // Re-enable turn detection now that greeting is done
            // Azure GA: skip session.update — turn detection is already configured in the token
            if (providerRef.current !== 'Azure OpenAI') {
              const turnDetectionConfig = voiceConfigRef.current?.turn_detection_type === 'semantic_vad'
                ? {
                  type: 'semantic_vad',
                  eagerness: voiceConfigRef.current.turn_detection_eagerness || 'medium',
                  create_response: voiceConfigRef.current.turn_detection_create_response ?? true,
                  interrupt_response: voiceConfigRef.current.turn_detection_interrupt_response ?? false,
                }
                : {
                  type: 'server_vad',
                  threshold: 0.5,
                  prefix_padding_ms: 300,
                  silence_duration_ms: 500,
                  create_response: voiceConfigRef.current?.turn_detection_create_response ?? true,
                }

              sendRealtimeEvent({
                type: 'session.update',
                session: {
                  turn_detection: turnDetectionConfig,
                  instructions: buildInstructions(voiceConfigRef.current?.system_instructions || '', i18next.language || 'de'),
                },
              })
            } else {
              // Azure GA: session.update not supported — language is controlled via
              // ?language= parameter passed to createSession()
              logInfo('Azure GA provider — language enforced via backend ?language= parameter')
            }
          }, estimatedDuration)
        }
        break
      }

      case 'response.audio.delta':
        // Audio chunks are handled by WebRTC audio stream automatically
        break

      case 'response.audio.done':
        updateState({ isSpeaking: false })
        setStatus('listening')
        break

      case 'input_audio_buffer.speech_started': {
        const speechEvent = event as InputAudioBufferSpeechStartedEvent
        logInfo(`User started speaking at ${speechEvent.audio_start_ms}ms`)
        updateState({ isListening: true })
        setStatus('listening')
        break
      }

      case 'input_audio_buffer.speech_stopped': {
        const speechEvent = event as InputAudioBufferSpeechStoppedEvent
        logInfo(`User stopped speaking at ${speechEvent.audio_end_ms}ms`)
        updateState({ isListening: false })
        setStatus('processing')
        break
      }

      case 'response.text.delta':
      case 'response.output_text.delta': {
        // Assistant text response delta (incremental updates)
        const delta = (event as any).delta
        if (delta) {
          logInfo(`Assistant text delta: ${delta}`)
        }
        break
      }

      case 'response.audio_transcript.delta': {
        // Partial audio transcript updates
        const delta = (event as any).delta
        if (delta) {
          logInfo(`Audio transcript delta: ${delta}`)
        }
        break
      }

      case 'response.audio_transcript.done':
        // Final audio transcript
        updateState({ isSpeaking: true })
        setStatus('speaking')
        break

      case 'conversation.item.input_audio_transcription.completed': {
        // User voice input transcription completed
        const transcriptionEvent = event as any
        const { item_id, transcript } = transcriptionEvent

        if (transcript) {
          logInfo(`User voice transcription completed: ${transcript}`)

          // Add user message to transcript
          const transcriptItem: TranscriptItem = {
            id: item_id,
            role: 'user',
            content: transcript,
            timestamp: new Date().toISOString(),
            type: 'message',
          }

          addTranscriptItem(transcriptItem)
        }
        break
      }

      case 'response.cancelled':
        logInfo('Response cancelled')
        updateState({ isSpeaking: false })
        setStatus('connected')
        break

      case 'response.failed':
        logError(new Error('Response failed'), {
          context: 'Realtime API',
          event,
        })
        updateState({ isSpeaking: false })
        setStatus('connected')
        break

      case 'error':
        logError(new Error(event.error?.message || 'Unknown Realtime API error'), {
          context: 'Realtime API',
          error: event.error,
        })
        updateState({ error: new Error(event.error?.message || 'Unknown error') })
        break

      default:
        // Ignore other event types
        break
    }
  }, [updateState, addTranscriptItem])

  /**
   * Send event to OpenAI via data channel
   */
  const sendRealtimeEvent = useCallback((event: RealtimeEvent) => {
    if (!dataChannelRef.current || dataChannelRef.current.readyState !== 'open') {
      logWarn(`Cannot send event: data channel not open - ${event.type}`)
      return
    }

    try {
      const message = JSON.stringify(event)
      dataChannelRef.current.send(message)
      logInfo(`Sent event to Realtime API: ${event.type}`)
    } catch (error) {
      logError(error as Error, { context: 'Send Realtime event' })
    }
  }, [])

  // Keep ref updated for use in event handlers
  sendRealtimeEventRef.current = sendRealtimeEvent

  /**
   * Connect to OpenAI Realtime API
   */
  const connect = useCallback(async () => {
    try {
      setStatus('connecting')
      // Reset sequence counter for new session
      sequenceCounterRef.current = 0
      voiceLineRef.current = NO_VOICE_LINE
      updateState({
        error: null,
        currentLine: '',
        transcript: [], // Clear transcript from previous sessions
      })

      logInfo('Creating Realtime session...')

      // Step 1: Get ephemeral token and client-specific config from cdb_tqs
      const sessionResponse = await realtimeSessionService.createSession(i18next.language)
      const ephemeralToken = sessionResponse.client_secret.value

      // Extract voice config from session response (includes client-specific prompts)
      const voiceConfig = {
        system_instructions: sessionResponse.system_instructions || 'You are a helpful AI voice assistant.',
        transcription_hint: sessionResponse.transcription_hint || '',
        greeting_instructions: sessionResponse.greeting_instructions || '',
        voice: 'coral', // Default voice
        model: 'gpt-4o-realtime-preview-2024-12-17',
        turn_detection_type: 'semantic_vad',
        turn_detection_eagerness: 'medium',
        turn_detection_create_response: true,
        turn_detection_interrupt_response: false,
      }

      voiceConfigRef.current = voiceConfig
      providerRef.current = sessionResponse.provider || 'openai'
      greetingCompletedRef.current = false // Reset on new connection
      greetingRequestedRef.current = false // Reset greeting request flag

      logInfo(`Session created: ${sessionResponse.session_id}, expires: ${sessionResponse.expires_at}`)

      // Step 2: Request microphone access
      logInfo('Requesting microphone access...')
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
      audioStreamRef.current = stream

      // Step 3: Create audio context
      audioContextRef.current = new AudioContext({ sampleRate: 24000 })

      // Step 4: Setup WebRTC peer connection
      const pc = new RTCPeerConnection()
      peerConnectionRef.current = pc

      // Add audio tracks
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream)
      })

      // Handle incoming audio with optimized playback
      pc.ontrack = (event) => {
        logInfo('Received remote audio track')

        // Clean up previous audio element if exists
        if (audioElementRef.current) {
          audioElementRef.current.pause()
          audioElementRef.current.srcObject = null
        }

        const audioElement = new Audio()
        audioElement.srcObject = event.streams[0]

        // Store reference for cleanup
        audioElementRef.current = audioElement

        // Play with error handling
        audioElement.play().catch(err => {
          logError(err as Error, { context: 'Audio playback failed' })
        })
      }

      // Step 5: Create data channel for events
      const dc = pc.createDataChannel('oai-events')
      dataChannelRef.current = dc

      dc.onopen = () => {
        logInfo('Data channel opened')
        updateState({ isConnected: true })
        setStatus('connected')

        // Mute mic during greeting to prevent echo/interruption (without changing user state)
        if (voiceConfig.greeting_instructions?.trim() && audioStreamRef.current) {
          audioStreamRef.current.getAudioTracks().forEach(track => {
            track.enabled = false
          })
        }

        // Send initial configuration event (WITHOUT greeting in instructions)
        const transcriptionConfig: any = {
          model: 'whisper-1',
        }

        // Add transcription hint if available
        if (voiceConfig.transcription_hint?.trim()) {
          transcriptionConfig.prompt = voiceConfig.transcription_hint.trim()
        }

        // Build turn_detection config based on type
        let turnDetectionConfig: any
        const turnDetectionType = voiceConfig.turn_detection_type || 'semantic_vad'

        if (turnDetectionType === 'semantic_vad') {
          // Use semantic_vad with eagerness (newer API)
          turnDetectionConfig = {
            type: 'semantic_vad',
            eagerness: voiceConfig.turn_detection_eagerness || 'medium',
            create_response: voiceConfig.turn_detection_create_response ?? true,
            interrupt_response: voiceConfig.turn_detection_interrupt_response ?? false,
          }
        } else {
          // Fallback to server_vad (legacy)
          turnDetectionConfig = {
            type: 'server_vad',
            threshold: 0.5,
            prefix_padding_ms: 300,
            silence_duration_ms: 500,
            create_response: voiceConfig.turn_detection_create_response ?? true,
          }
        }

        // Build instructions: prepend language instruction, then base instructions
        // IMPORTANT: read i18next.language directly (not languageRef) to avoid stale ref from settings sync
        const currentLang = i18next.language || 'de'
        let fullInstructions = buildInstructions(voiceConfig.system_instructions || '', currentLang)

        // If greeting is configured, add strict instruction for first message
        if (voiceConfig.greeting_instructions?.trim()) {
          fullInstructions += `\n\n${i18next.t('voiceAgent.greetingSessionInstruction', { greetingText: voiceConfig.greeting_instructions })}`
        }

        // Azure GA: session config is baked into the ephemeral token.
        // session.update via data channel is NOT supported — skip it entirely.
        if (providerRef.current === 'Azure OpenAI') {
          logInfo('Azure GA provider — skipping session.update (config baked into token)')
          // Greeting will be triggered in session.created handler
        } else {
          sendRealtimeEvent({
            type: 'session.update',
            session: {
              modalities: ['text', 'audio'],
              instructions: fullInstructions,
              voice: voiceConfig.voice || 'alloy',
              input_audio_format: 'pcm16',
              output_audio_format: 'pcm16',
              input_audio_transcription: transcriptionConfig,
              // During greeting: Disable turn detection completely
              // This prevents the greeting from being interrupted by any detected speech
              turn_detection: voiceConfig.greeting_instructions?.trim()
                ? null
                : turnDetectionConfig,
            },
          })

          // NOTE: Initial greeting is now triggered in session.updated handler
          // This ensures the session config is confirmed before requesting a response
        }
      }

      dc.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          handleRealtimeEvent(data)
        } catch (error) {
          logError(error as Error, { context: 'Parse Realtime event', data: event.data })
        }
      }

      dc.onerror = (error) => {
        logError(error as unknown as Error, { context: 'Data channel error' })
      }

      dc.onclose = () => {
        logInfo('Data channel closed')
        updateState({ isConnected: false })
        setStatus('disconnected')
      }

      // Step 6: Create offer and set local description
      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)

      // Step 7: Connect to Realtime API with ephemeral token
      // Use the realtime_url from the backend (Azure or OpenAI) instead of hardcoded URL
      const realtimeUrl = (sessionResponse.realtime_url || OPENAI_REALTIME_URL_FALLBACK).replace(/\/+$/, '')
      const realtimeModel = sessionResponse.model || 'gpt-4o-realtime-preview-2024-12-17'
      logInfo(`Connecting to Realtime API (${sessionResponse.provider || 'openai'}) at ${realtimeUrl}...`)
      let answerSdp: string
      if (sessionResponse.relay_url) {
        // The Azure account only accepts the backend, so TQS relays the offer (cdb_tqs#1747)
        answerSdp = await realtimeSessionService.exchangeSdp(sessionResponse.relay_url, ephemeralToken, offer.sdp ?? '')
      } else {
        const response = await fetch(`${realtimeUrl}?model=${realtimeModel}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${ephemeralToken}`,
            'Content-Type': 'application/sdp',
          },
          body: offer.sdp,
        })

        if (!response.ok) {
          throw new Error(`Failed to connect to OpenAI: ${response.status} ${response.statusText}`)
        }
        answerSdp = await response.text()
      }

      // Step 8: Set remote description from OpenAI
      await pc.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp,
      })

      logInfo('Successfully connected to OpenAI Realtime API')
      // Note: Initial events will be sent once data channel opens (dc.onopen)

    } catch (error) {
      logError(error as Error, { context: 'Connect to Realtime API' })
      updateState({ error: error as Error })
      setStatus('disconnected')

      // Cleanup on error
      if (dataChannelRef.current) {
        dataChannelRef.current.close()
        dataChannelRef.current = null
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close()
        peerConnectionRef.current = null
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop())
        audioStreamRef.current = null
      }
      if (audioContextRef.current) {
        audioContextRef.current.close()
        audioContextRef.current = null
      }
      if (audioElementRef.current) {
        audioElementRef.current.pause()
        audioElementRef.current.srcObject = null
        audioElementRef.current = null
      }

      // Reset greeting flags and provider
      greetingCompletedRef.current = false
      greetingRequestedRef.current = false
      voiceConfigRef.current = null
      providerRef.current = 'openai'
    }
  }, [updateState, handleRealtimeEvent, sendRealtimeEvent])

  /**
   * Disconnect from Realtime API
   */
  const disconnect = useCallback(() => {
    // Reset sequence counter
    sequenceCounterRef.current = 0
    // Only log if actually connected (avoid noisy logs on unmount)
    const wasConnected = peerConnectionRef.current !== null || dataChannelRef.current !== null
    if (wasConnected) {
      logInfo('Disconnecting from Realtime API')
    }

    // Close data channel
    if (dataChannelRef.current) {
      dataChannelRef.current.close()
      dataChannelRef.current = null
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close()
      peerConnectionRef.current = null
    }

    // Stop audio tracks
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop())
      audioStreamRef.current = null
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }

    // Stop audio playback
    if (audioElementRef.current) {
      audioElementRef.current.pause()
      audioElementRef.current.srcObject = null
      audioElementRef.current = null
    }

    // Reset greeting flags and provider
    greetingCompletedRef.current = false
    greetingRequestedRef.current = false
    voiceConfigRef.current = null
    providerRef.current = 'openai'

    updateState({
      isConnected: false,
      isListening: false,
      isSpeaking: false,
      transcript: [], // Clear transcript on disconnect
      currentLine: '',
    })
    setStatus('disconnected')
    setIsMinimized(false) // Reset minimized state on disconnect
  }, [updateState])

  /**
   * Toggle mute
   */
  const toggleMute = useCallback(() => {
    if (audioStreamRef.current) {
      const audioTracks = audioStreamRef.current.getAudioTracks()
      const newMutedState = !state.isMuted
      audioTracks.forEach(track => {
        track.enabled = !newMutedState // If muted, disable track
      })
      updateState({ isMuted: newMutedState })
      logInfo(`Audio ${newMutedState ? 'muted' : 'unmuted'}`)
    }
  }, [state.isMuted, updateState])

  /**
   * Clear transcript
   */
  const clearTranscript = useCallback(() => {
    sequenceCounterRef.current = 0 // Reset sequence counter
    updateState({ transcript: [] })
  }, [updateState])

  /**
   * Toggle minimize widget
   */
  const toggleMinimize = useCallback(() => {
    setIsMinimized(prev => !prev)
  }, [])

  /**
   * Send a text message
   */
  const sendMessage = useCallback((text: string) => {
    sendRealtimeEvent({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [
          {
            type: 'input_text',
            text,
          },
        ],
      },
    })

    // Add to transcript immediately
    addTranscriptItem({
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
      type: 'message',
    })

    // Request response
    sendRealtimeEvent({
      type: 'response.create',
    })
  }, [sendRealtimeEvent, addTranscriptItem])

  /**
   * Session duration timer
   */
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null
    if (state.isConnected) {
      interval = setInterval(() => {
        setSessionDuration((prev) => prev + 1)
      }, 1000)
    } else {
      setSessionDuration(0)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [state.isConnected])

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [disconnect])

  /**
   * Dynamically react to UI language changes while a session is active.
   * - OpenAI: send session.update with updated instructions (no reconnect needed)
   * - Azure GA: reconnect so a new token with the correct language is issued
   */
  useEffect(() => {
    const handleLanguageChange = (newLang: string) => {
      // No session active — nothing to do, language will be picked up on next connect()
      const isOpen = dataChannelRef.current?.readyState === 'open'
      if (!isOpen) return

      if (providerRef.current === 'Azure OpenAI') {
        logInfo(`Language changed to "${newLang}" — reconnecting Azure session`)
        disconnect()
        // Small delay to ensure cleanup finishes before opening a new connection
        setTimeout(() => connect(), 300)
      } else {
        logInfo(`Language changed to "${newLang}" — updating OpenAI session instructions`)
        sendRealtimeEvent({
          type: 'session.update',
          session: {
            instructions: buildInstructions(voiceConfigRef.current?.system_instructions || '', newLang),
          },
        })
      }
    }

    i18next.on('languageChanged', handleLanguageChange)
    return () => { i18next.off('languageChanged', handleLanguageChange) }
  }, [connect, disconnect, sendRealtimeEvent])

  return {
    state,
    status,
    connect,
    disconnect,
    toggleMute,
    clearTranscript,
    sendMessage,
    isMinimized,
    toggleMinimize,
    sessionDuration,
  }
}
